import type { PartnerCustomer as Customer, PartnerCustomerDetail } from '@central/api';
import { Card, HealthChip, PageHeader, StatusChip, formatAgo } from '@central/ui';
import { Button, Group, Modal, Table, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../../api';
import { Crumbs, DataTable, Empty, QueryState } from '../../components/parts';
import { customerStatusChip, enrollmentLabel, muted } from '../../lib/labels';
import { Form } from '../CustomerDialogs';

const nameRequired = 'Enter the customer’s name.';
const fieldsOf = (e: unknown) => (e as { fields?: Record<string, string> }).fields;

/**
 * A reseller's customers: the ones it added and the ones Rethink assigned
 * to it.
 */
export function PartnerCustomers() {
  const api = useApi();
  const [adding, setAdding] = useState(false);
  const list = useQuery({
    queryKey: keys.partnerCustomers,
    queryFn: () => api.partner.customers.list(),
  });
  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="The customers you install and support. Rethink licenses them."
        actions={
          <Button color="dark" size="sm" h={40} onClick={() => setAdding(true)}>
            New customer
          </Button>
        }
      />
      {adding && <NewPartnerCustomer onClose={() => setAdding(false)} />}
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            list.data!.items.length === 0 ? (
              <Empty>No customers yet. Add one, or ask Rethink to assign yours.</Empty>
            ) : (
              <DataTable label="Customers" minWidth={480} head={['Name', 'Status', 'Environments']}>
                {list.data!.items.map((c) => {
                  const chip = customerStatusChip[c.status];
                  return (
                    <Table.Tr key={c.id}>
                      <Table.Td style={{ fontWeight: 600 }}>
                        <Link to="/partner/customers/$customerId" params={{ customerId: c.id }}>
                          {c.name}
                        </Link>
                      </Table.Td>
                      <Table.Td>
                        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
                      </Table.Td>
                      <Table.Td>{c.environments}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </DataTable>
            )
          }
        </QueryState>
      </Card>
    </>
  );
}

/** One of the reseller's customers and the environments it sees for it. */
export function PartnerCustomer() {
  const { customerId } = useParams({ from: '/app/partner/customers/$customerId' });
  const api = useApi();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const q = useQuery({
    queryKey: keys.partnerCustomer(customerId),
    queryFn: () => api.partner.customers.get(customerId),
  });
  return (
    <QueryState isPending={q.isPending} error={q.error}>
      {() => {
        const { customer: c, environments } = q.data!;
        const chip = customerStatusChip[c.status];
        return (
          <>
            <Crumbs items={[{ label: 'Customers', to: '/partner/customers' }, { label: c.name }]} />
            <PageHeader
              title={c.name}
              subtitle={<StatusChip tone={chip.tone}>{chip.label}</StatusChip>}
              actions={
                <Group gap={8}>
                  <Button variant="default" h={40} onClick={() => setEditing(true)}>
                    Edit details
                  </Button>
                  <Button
                    variant="default"
                    h={40}
                    onClick={() => void navigate({ to: '/inbox', search: { customerId: c.id } })}
                  >
                    Conversations
                  </Button>
                  <Button
                    color="dark"
                    h={40}
                    onClick={() =>
                      void navigate({
                        to: '/partner/environments',
                        search: { customerId: c.id, new: true },
                      })
                    }
                  >
                    Add environment
                  </Button>
                </Group>
              }
            />
            {editing && <PartnerCustomerDetails c={c} onClose={() => setEditing(false)} />}
            <Card title={`Environments · ${environments.length}`} padded={false}>
              {environments.length === 0 ? (
                <Empty>
                  No environments yet. Add one, then enroll the installation with its token.
                </Empty>
              ) : (
                <DataTable
                  label="Environments"
                  minWidth={640}
                  head={['Name', 'Health', 'Version', 'Enrollment', 'Last report']}
                >
                  {environments.map((e) => (
                    <Table.Tr key={e.id}>
                      <Table.Td style={{ fontWeight: 600 }}>
                        <Link
                          to="/partner/environments/$environmentId"
                          params={{ environmentId: e.id }}
                        >
                          {e.name}
                        </Link>
                      </Table.Td>
                      <Table.Td>
                        <HealthChip state={e.health} />
                      </Table.Td>
                      <Table.Td>{e.version ?? '–'}</Table.Td>
                      <Table.Td>{enrollmentLabel[e.enrollment.status]}</Table.Td>
                      <Table.Td style={muted}>
                        {e.lastHeartbeatAt ? formatAgo(e.lastHeartbeatAt) : 'Never'}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </DataTable>
              )}
            </Card>
          </>
        );
      }}
    </QueryState>
  );
}

/**
 * A new customer of the reseller: its name only. Rethink licenses it; the
 * reseller and account manager are not the reseller's to set.
 */
function NewPartnerCustomer({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string>();
  const create = useMutation({
    mutationFn: () => api.partner.customers.create({ name: name.trim() }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: keys.partnerCustomers });
      await navigate({ to: '/partner/customers/$customerId', params: { customerId: c.id } });
    },
    onError: (e) => {
      if (fieldsOf(e)?.name) setNameError(nameRequired);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setNameError(nameRequired);
    setNameError(undefined);
    create.mutate();
  };
  return (
    <Modal opened onClose={onClose} title="New customer">
      <Form
        onSubmit={submit}
        error={nameError ? undefined : create.error}
        submitLabel="Create customer"
        pending={create.isPending}
        onCancel={onClose}
      >
        <TextInput
          label="Name"
          data-autofocus
          value={name}
          error={nameError}
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </Form>
    </Modal>
  );
}

/** Rename one of the reseller's customers; an unchanged name sends nothing. */
function PartnerCustomerDetails({ c, onClose }: { c: Customer; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState(c.name);
  const [nameError, setNameError] = useState<string>();
  const save = useMutation({
    mutationFn: (next: string) => api.partner.customers.update(c.id, { name: next }),
    onSuccess: async (next) => {
      queryClient.setQueryData<PartnerCustomerDetail>(keys.partnerCustomer(c.id), (d) =>
        d ? { ...d, customer: next } : d,
      );
      await queryClient.invalidateQueries({ queryKey: keys.partnerCustomers, exact: true });
      onClose();
    },
    onError: (e) => {
      if (fieldsOf(e)?.name) setNameError(nameRequired);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setNameError(nameRequired);
    setNameError(undefined);
    if (name.trim() === c.name) return onClose();
    save.mutate(name.trim());
  };
  return (
    <Modal opened onClose={onClose} title="Customer details">
      <Form
        onSubmit={submit}
        error={nameError ? undefined : save.error}
        submitLabel="Save details"
        pending={save.isPending}
        onCancel={onClose}
      >
        <TextInput
          label="Name"
          data-autofocus
          value={name}
          error={nameError}
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </Form>
    </Modal>
  );
}
